import axios from 'axios';
import { auth } from '../firebase/config';
import { getStoredApiKey } from '../utils/apiKey';
import { env } from '../config/env';
import { ANALYSIS_TIMEOUT_MS, UPLOAD_TIMEOUT_MS } from '../config/analysis';

const api = axios.create({
  baseURL: env.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

function llmApiKeyHeaders(): Record<string, string> {
  const apiKey = getStoredApiKey();
  return apiKey ? { 'X-LLM-API-Key': apiKey } : {};
}

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  created_at: string;
  analysis?: ConversationAnalysis;
}

export interface ConversationAnalysis {
  conversation_id: string;
  overall_score: number;
  communication_clarity_score: number;
  efficiency_score: number;
  practical_impact_score: number;
  enhanced_prompts: string[];
  feedback_points: string[];
  strengths: string[];
  weaknesses: string[];
  suggestions: string[];
  safety_flags?: string[];
  created_at: string;
}

export interface AnalysisResult {
  analysis_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  result?: ConversationAnalysis;
  error?: string;
}

function normalizeConversationsPayload(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }

  if (data && typeof data === 'object' && 'conversations' in data) {
    const conversations = (data as { conversations: unknown }).conversations;
    if (Array.isArray(conversations)) {
      return conversations as Record<string, unknown>[];
    }
    if (conversations && typeof conversations === 'object') {
      return [conversations as Record<string, unknown>];
    }
  }

  throw new Error('Invalid server response: expected a list of conversations.');
}

export interface UploadResult {
  message: string;
  conversation_ids: string[];
  new_conversations: number;
  duplicates_skipped: number;
}

/** Parse JSON file via backend (legacy; prefer uploadConversationsFile). */
export const parseConversationsFile = async (file: File): Promise<{ conversations: Record<string, unknown>[] }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/conversations/parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: UPLOAD_TIMEOUT_MS,
  });

  const conversations = normalizeConversationsPayload(response.data);
  return { conversations };
};

/** Parse and save conversations via backend (batched Firestore writes server-side). */
export const uploadConversationsFile = async (file: File): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/conversations/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: UPLOAD_TIMEOUT_MS,
  });

  return response.data;
};

export const analyzeConversation = async (conversationId: string): Promise<{
  message: string;
  analysis_id: string;
  status: string;
  analysis?: ConversationAnalysis;
}> => {
  const response = await api.post(`/conversations/${conversationId}/analyze`, null, {
    headers: llmApiKeyHeaders(),
    timeout: ANALYSIS_TIMEOUT_MS,
  });
  return response.data;
};

export const healthCheck = async (): Promise<{ status: string; redis: boolean }> => {
  const response = await api.get('/health');
  return response.data;
};

export const validateLlmApiKey = async (apiKey: string): Promise<{ valid: boolean; error?: string }> => {
  const response = await api.post(
    '/llm-api-key/validate',
    null,
    {
      headers: { 'X-LLM-API-Key': apiKey.trim() },
    },
  );
  return response.data;
};
